import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';
import SingleCheckbox from '@/common/components/form-inputs/SingleCheckbox';
import AppSingleSelectCheckboxGroup from '@/common/components/form-inputs/AppSingleSelectCheckboxGroup';
import { RESULT_CAN_CONTACTED_TARGET_LIST } from '@/common/constants/common';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const resultFields = [
	'resultHasContacted',
	'resultCanContacted',
	'resultNeedDiscuss',
	'resultNotSuitable',
];

// Follow-up fields that only apply while their result is ticked, and the value
// they fall back to once it is not.
const dependentFields: Record<string, Record<string, any>> = {
	resultCanContacted: { resultCanContactedTarget: null },
	resultNotSuitable: { resultNotSuitableBecause: '' },
};

const EditResultsForm = forwardRef(({ type, setType, details }: Props, ref) => {
	const { t } = useTranslation();
	const defaultValues: DefaultValues<any> = {
		resultCanContactedTarget: null,
	};

	useEffect(() => {
		if (details) methods.reset(details);
	}, [details]);

	const formSchema = Yup.object().shape({
		resultNotSuitableBecause: Yup.string().when('resultNotSuitable', {
			is: (is: boolean) => is,
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),
		resultCanContactedTarget: Yup.number()
			.nullable()
			.when('resultCanContacted', {
				is: (is: boolean) => is,
				then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			}),
		dateOfInformOic: Yup.string()
			.nullable()
			.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
		dateOfConfirmationToReferrer: Yup.string()
			.nullable()
			.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
	});
	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
		mode: 'onChange',
		shouldFocusError: true,
	});

	const watchResultNotSuitable = methods.watch('resultNotSuitable');
	const watchResultCanContacted = methods.watch('resultCanContacted');

	const handleExclusiveChange = (name: string) => (checked: boolean) => {
		// Only one result may be selected at a time, so ticking one clears the
		// other three; either way, every result that ends up unticked drops the
		// follow-up field hanging off it rather than submitting it stale.
		const untickedFields = checked
			? resultFields.filter((field) => field !== name)
			: [name];
		untickedFields.forEach((field) => {
			if (field !== name) methods.setValue(field, false);
			Object.entries(dependentFields[field] ?? {}).forEach(
				([dependent, empty]) => {
					methods.setValue(dependent, empty);
					methods.clearErrors(dependent);
				}
			);
		});
	};

	useImperativeHandle(ref, () => ({
		getFormData: () => methods.getValues(),
		validateForm: () => {
			methods.trigger();
			methods.formState.isValid;
			return methods.trigger().then(() => methods.formState.isValid);
		},
	}));

	const onSubmit: SubmitHandler<any> = async (data) => {};

	return (
		<>
			<FormProvider {...methods}>
				<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
					{t('preAdmission.results')}
				</Typography>
				<form onSubmit={methods.handleSubmit((e) => e.preventDefault())}>
					<Grid container spacing={1} marginTop={0} marginBottom={3}>
						<Grid size={12}>
							<SingleCheckbox
								control={methods.control}
								name='resultHasContacted'
								label={t('preAdmission.resultsText1')}
								handleChange={handleExclusiveChange('resultHasContacted')}
							/>
						</Grid>
						<Grid size={12}>
							{/* Reads as one sentence: the choice sits where the paper form
							    has "the case / the carer" for the worker to circle. */}
							<Box display={'flex'}>
								<SingleCheckbox
									sx={{
										flexShrink: 0,
										'& .MuiFormControlLabel-label': { whiteSpace: 'nowrap' },
									}}
									control={methods.control}
									name='resultCanContacted'
									label={`${t('preAdmission.resultsText2Before')}${
										watchResultCanContacted ? '*' : ''
									}`}
									handleChange={handleExclusiveChange('resultCanContacted')}
								/>
								<AppSingleSelectCheckboxGroup
									row
									separator='/'
									control={methods.control}
									name='resultCanContactedTarget'
									options={RESULT_CAN_CONTACTED_TARGET_LIST}
									disabled={!watchResultCanContacted}
									// Pull the group back by the label margin zeroed below, so the
									// first checkbox keeps sitting where it did.
									style={{
										width: 'auto',
										flexShrink: 0,
										marginLeft: '-11px',
										marginRight: '8px',
									}}
									itemStyle={{ marginLeft: 0, marginRight: 0 }}
								/>
								<Typography
									variant='body1'
									sx={{ whiteSpace: 'nowrap', marginTop: '9px' }}
								>
									{t('preAdmission.resultsText2After')}
								</Typography>
							</Box>
						</Grid>
						<Grid size={12}>
							<SingleCheckbox
								control={methods.control}
								name='resultNeedDiscuss'
								label={t('preAdmission.resultsText3')}
								handleChange={handleExclusiveChange('resultNeedDiscuss')}
							/>
						</Grid>
						<Grid size={12}>
							<Box display={'flex'}>
								<SingleCheckbox
									sx={{
										flexShrink: 0,
										'& .MuiFormControlLabel-label': {
											whiteSpace: 'nowrap',
											overflow: 'hidden',
											textOverflow: 'ellipsis',
										},
									}}
									control={methods.control}
									name='resultNotSuitable'
									label={t('preAdmission.resultsText4')}
									handleChange={handleExclusiveChange('resultNotSuitable')}
								/>
								<AppTextField
									disabled={!watchResultNotSuitable}
									sx={{ position: 'relative', top: '-10px' }}
									variantType={'standard'}
									control={methods.control}
									name='resultNotSuitableBecause'
									label={`${t('preAdmission.because')}${
										watchResultNotSuitable ? '*' : ''
									}`}
								/>
							</Box>
						</Grid>
						<Grid size={6}>
							<AppDatePicker
								maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
								control={methods.control}
								name='dateOfInformOic'
								label={t('preAdmission.informDate')}
							/>
						</Grid>
						<Grid size={6}>
							<AppDatePicker
								maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
								control={methods.control}
								name='dateOfConfirmationToReferrer'
								label={t('preAdmission.confirmationDate')}
							/>
						</Grid>
					</Grid>
				</form>
			</FormProvider>
		</>
	);
});

EditResultsForm.displayName = 'EditResultsForm';

export default EditResultsForm;
